import { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  absoluteNavUrl,
  filterMainNavSections,
  type NavItem,
} from "../../config/navigation";
import { useAuth } from "../../stores/auth";
import { useShellSidebar } from "../../stores/shell-sidebar";
import { useShellTabs } from "../../stores/shell-tabs";
import { Icon } from "../icons";
import { NavContextMenu, type NavContextMenuState } from "./NavContextMenu";

export function CollapsedSidebar() {
  const { can, moduleEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const openInActiveTab = useShellTabs((s) => s.openInActiveTab);
  const openInNewTab = useShellTabs((s) => s.openInNewTab);
  const [menu, setMenu] = useState<NavContextMenuState | null>(null);
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const pinned = useShellSidebar((s) => s.pinned);
  const expanded = pinned || hoverExpanded;

  const sections = useMemo(
    () => filterMainNavSections(can, moduleEnabled),
    [can, moduleEnabled],
  );

  const handleNavClick = (path: string) => {
    const next = openInActiveTab(path);
    navigate(next);
  };

  const openContextMenu = (event: React.MouseEvent, item: NavItem) => {
    event.preventDefault();
    setMenu({ x: event.clientX, y: event.clientY, path: item.to, label: item.label });
  };

  const copyLink = async (path: string) => {
    try {
      await navigator.clipboard.writeText(absoluteNavUrl(path));
    } catch {
      // ignore
    }
  };

  return (
    <>
      {/* Fixed rail width in layout; panel expands as overlay on hover */}
      <div
        className="relative shrink-0 transition-[width] duration-200 ease-out"
        style={{ width: pinned ? "var(--nav-sidebar-expanded-width)" : "var(--nav-sidebar-width)" }}
        onMouseEnter={() => {
          if (!pinned) setHoverExpanded(true);
        }}
        onMouseLeave={() => {
          if (!pinned) setHoverExpanded(false);
        }}
      >
        <aside
          className={`shell-sidebar flex flex-col border-r border-line bg-surface ${
            pinned ? "relative h-full" : "absolute inset-y-0 left-0 z-30 shadow-[var(--theme-sidebar-shadow)]"
          }`}
          data-expanded={expanded ? "true" : "false"}
          data-pinned={pinned ? "true" : "false"}
        >
          <nav className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto py-1">
            {sections.map((section, sectionIndex) => (
              <div key={section.title ?? sectionIndex}>
                {sectionIndex > 0 && (
                  <div className="mx-1.5 my-0.5 border-t border-line" aria-hidden="true" />
                )}
                {expanded && section.title && (
                  <p className="mb-0.5 px-2.5 text-[9px] font-semibold uppercase tracking-widest text-muted-2">
                    {section.title}
                  </p>
                )}
                <div className="flex flex-col gap-px px-1">
                  {section.items.map((item) => {
                    const active =
                      item.to === "/"
                        ? location.pathname === "/"
                        : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/"}
                        title={expanded ? undefined : item.label}
                        onClick={(event) => {
                          event.preventDefault();
                          handleNavClick(item.to);
                        }}
                        onContextMenu={(event) => openContextMenu(event, item)}
                        className={() =>
                          `flex h-8 w-full items-center rounded-lg transition-colors ${
                            expanded ? "gap-2 px-2" : "justify-center"
                          } ${
                            active
                              ? "bg-gradient-brand font-medium text-white shadow-sm shadow-brand-pink/15"
                              : "text-muted hover:bg-hover hover:text-foreground"
                          }`
                        }
                      >
                        <Icon icon={item.icon} size="sm" className="shrink-0 opacity-90" />
                        {expanded && (
                          <span className="min-w-0 flex-1 truncate text-xs whitespace-nowrap">{item.label}</span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>
      </div>

      <NavContextMenu
        menu={menu}
        onClose={() => setMenu(null)}
        onOpenInNewTab={(path) => {
          const next = openInNewTab(path);
          navigate(next);
        }}
        onOpenLink={(path) => {
          const next = openInActiveTab(path);
          navigate(next);
        }}
        onCopyLink={(path) => void copyLink(path)}
      />
    </>
  );
}
